import { cn } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';
import { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import duolingoLogo from '@/assets/icons/duolingo.svg';
import motorolaLogo from '@/assets/icons/motorola.svg';
import oktaLogo from '@/assets/icons/okta.svg';
import writerLogo from '@/assets/icons/writer.svg';
import hearstLogo from '@/assets/icons/hearst.svg';
import zendeskLogo from '@/assets/icons/zendesk.svg';
import mainLogo from '@/assets/icons/main_logo.svg';

interface CredibilityItemProps {
  title: string;
  subtitle: string;
}

export function CredibilityItem({ title, subtitle }: CredibilityItemProps) {
  return (
    <div
      data-testid="credibility-item"
      className="relative z-2 mx-9.5 flex-1 border-b border-solid pb-2 [border-image:linear-gradient(to_right,#fff,#fff,#0000)_30]"
    >
      <h2 className="m-0 text-xl font-semibold text-inherit">{title}</h2>
      <p className="text-md m-0 font-normal text-inherit">{subtitle}</p>
    </div>
  );
}

export function LogoItem({
  src,
  alt,
  className
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return (
    <div
      data-testid="logo-item"
      className={cn(
        'm-0 box-border w-full p-4 xl:max-w-4/12 xl:basis-4/12',
        className
      )}
    >
      <img src={src} alt={alt} className="mx-auto block" />
    </div>
  );
}

export function AuthLayout({ children }: PropsWithChildren) {
  return (
    <div data-testid="auth-layout" className="flex min-h-svh">
      <div className="flex shrink grow flex-col gap-4 p-2 md:p-6">
        <div className="-m-2 flex justify-center gap-2 p-6 md:-m-6 md:justify-start">
          <Link to={ROUTES.root()} className="flex items-center gap-2 font-medium">
            <img src={mainLogo} alt="TopLens" className="h-6 w-auto" />
          </Link>
        </div>
        <div className="flex size-full items-center justify-center">
          <div className="w-full max-w-[400px]">{children}</div>
        </div>
      </div>
      <div className="relative hidden h-full min-h-svh basis-[614px] overflow-hidden bg-gradient-login-hero bg-cover bg-center bg-no-repeat py-24 font-banner lg:flex lg:flex-col lg:items-center lg:justify-center">
        <div className="right-content-wrapper flex flex-col items-center justify-center">
          <div className="relative w-[520px] rounded-[12px] border border-transparent shadow-[0_1px_25px_hsla(0,0%,100%,.051)] [background:radial-gradient(circle_at_50%_50%,rgba(23,18,48,.839)_42%,#5277ff_100%)_17.83%_23.88%/183%_215.6%,radial-gradient(circle_at_50%_50%,rgba(23,18,48,.839)_42%,#5277ff_100%)_50.85%_19.18%/170.8%_178.2%,radial-gradient(ellipse_at_44%_84%,rgba(23,18,48,.722)_0,#5277ff_100%)_11.39%_37.89%/258%_119%] before:absolute before:inset-[0.5px] before:z-1 before:rounded-[10px] before:content-[''] before:[background:radial-gradient(ellipse_at_34%_47%,#223070_15%,#09123f_30%)_-320px_-580px/1400px_1500px]">
            <div className="relative z-2 flex flex-col justify-center gap-10 p-8 text-primary-foreground">
              <CredibilityItem title="140+" subtitle="Countries Served" />
              <CredibilityItem title="25,000+" subtitle="Clients Served" />
              <CredibilityItem title="300+" subtitle="Industries" />
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center">
            <div className="mt-10 mb-8">
              <p className="text-sm font-normal m-0 text-center text-muted uppercase opacity-70">
                TRUSTED BY THOUSANDS OF LEADING BRANDS
              </p>
            </div>
            <div className="-m-4 -ml-[5px] box-border flex min-h-[120px] w-[calc(100%+32px)] max-w-[457px] flex-row flex-wrap items-center justify-start">
              <LogoItem src={duolingoLogo} alt="Duolingo" />
              <LogoItem src={motorolaLogo} alt="Motorola" />
              <LogoItem src={oktaLogo} alt="Okta" />
              <LogoItem src={writerLogo} alt="Writer" />
              <LogoItem src={hearstLogo} alt="Hearst" />
              <LogoItem src={zendeskLogo} alt="Zendesk" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
